import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Calendar, DollarSign, Activity, Edit, Trash2, ClipboardList, FileText, GanttChart, Package, Truck, Users } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { getUser } from "../utils/authService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    start_date: "",
    end_date: "",
    total_budget: "",
    description: ""
  });

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.role === "Master Admin" || currentUser.email === "shiva.mittal@kpminfra.com";

  useEffect(() => {
    fetchProjects();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await apiClient.get('/permissions');
      setUserPermissions(response.data.permissions || []);
    } catch (error) {
      console.error("Failed to fetch permissions");
    }
  };

  const hasModuleAccess = (moduleId) => {
    // Master admin has access to everything
    if (isMasterAdmin || userPermissions.includes("all")) return true;
    return userPermissions.includes(moduleId);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await apiClient.put(`/projects/${editingProject.id}`, formData);
        toast.success("Project updated successfully");
      } else {
        await apiClient.post('/projects', formData);
        toast.success("Project created successfully");
      }
      setDialogOpen(false);
      setEditingProject(null);
      setFormData({
        name: "",
        location: "",
        start_date: "",
        end_date: "",
        total_budget: "",
        description: ""
      });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingProject ? 'update' : 'create'} project`);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      location: project.location,
      start_date: project.start_date,
      end_date: project.end_date,
      total_budget: project.total_budget,
      description: project.description || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete project "${projectName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/projects/${projectId}`);
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const openNewProjectDialog = () => {
    setEditingProject(null);
    setFormData({
      name: "",
      location: "",
      start_date: "",
      end_date: "",
      total_budget: "",
      description: ""
    });
    setDialogOpen(true);
  };

  if (loading) {
    return <div className="text-center py-12">Loading projects...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">PROJECTS</h1>
          <p className="text-slate-600 mt-2 font-body">Manage your road construction projects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button onClick={openNewProjectDialog} data-testid="create-project-button" className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>New Project</span>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{editingProject ? 'EDIT PROJECT' : 'CREATE NEW PROJECT'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project Name</label>
                <input
                  type="text"
                  data-testid="project-name-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="NH-44 Road Widening Project"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Location</label>
                <input
                  type="text"
                  data-testid="project-location-input"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Delhi to Agra, Km 45-60"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date</label>
                  <input
                    type="date"
                    data-testid="project-start-date-input"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date</label>
                  <input
                    type="date"
                    data-testid="project-end-date-input"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Total Budget (₹)</label>
                <input
                  type="number"
                  data-testid="project-budget-input"
                  required
                  value={formData.total_budget}
                  onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  placeholder="50000000"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description</label>
                <textarea
                  data-testid="project-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Brief description of the project..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setDialogOpen(false);
                    setEditingProject(null);
                  }}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" data-testid="submit-project-button" className="btn-primary">
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
          <Activity className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h3 className="font-heading font-bold text-xl mb-2">No Projects Yet</h3>
          <p className="text-slate-600 mb-6">Create your first project to get started</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              data-testid={`project-card-${project.id}`}
              className="stat-card hover:shadow-lg transition-shadow group relative"
            >
              {isMasterAdmin && (
                <div className="absolute top-4 right-4 flex space-x-2 z-10">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEdit(project);
                    }}
                    data-testid={`edit-project-${project.id}`}
                    className="p-2 bg-white border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                    title="Edit Project"
                  >
                    <Edit className="w-4 h-4 text-primary" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDelete(project.id, project.name);
                    }}
                    data-testid={`delete-project-${project.id}`}
                    className="p-2 bg-white border border-red-300 rounded-sm hover:bg-red-50 transition-colors"
                    title="Delete Project"
                  >
                    <Trash2 className="w-4 h-4 text-error" />
                  </button>
                </div>
              )}
              
              <Link to={`/dashboard/${project.id}`} className="block">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 pr-16">
                    <h3 className="font-heading font-bold text-xl text-primary mb-1 group-hover:text-accent transition-colors">{project.name}</h3>
                    <div className="flex items-center text-sm text-slate-600 mt-2">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-sm text-xs font-medium uppercase tracking-wide ${
                    project.status === "Active" ? "bg-success/10 text-success" : "bg-slate-200 text-slate-700"
                  }`}>
                    {project.status}
                  </span>
                </div>

                <div className="space-y-2 mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Duration
                    </span>
                    <span className="font-mono text-primary font-medium">{project.start_date} to {project.end_date}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 flex items-center">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Budget
                    </span>
                    <span className="font-mono text-primary font-bold">₹{(project.total_budget / 10000000).toFixed(2)} Cr</span>
                  </div>
                </div>
              </Link>

                {/* Quick Access Links - Outside parent Link to avoid nested <a> tags */}
                {/* Only show modules user has permission to access */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-200">
                  {hasModuleAccess("progress") && (
                  <Link 
                    to={`/planning/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-primary/5 hover:bg-primary/10 transition-colors text-center"
                    data-testid={`planning-link-${project.id}`}
                  >
                    <GanttChart className="w-5 h-5 text-primary mb-1" />
                    <span className="text-xs font-medium text-primary">Planning</span>
                  </Link>
                  )}
                  {hasModuleAccess("boq") && (
                  <Link 
                    to={`/boq/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-accent/5 hover:bg-accent/10 transition-colors text-center"
                    data-testid={`boq-link-${project.id}`}
                  >
                    <ClipboardList className="w-5 h-5 text-accent mb-1" />
                    <span className="text-xs font-medium text-accent">BOQ</span>
                  </Link>
                  )}
                  {hasModuleAccess("dpr") && (
                  <Link 
                    to={`/dpr/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-success/5 hover:bg-success/10 transition-colors text-center"
                    data-testid={`dpr-link-${project.id}`}
                  >
                    <FileText className="w-5 h-5 text-success mb-1" />
                    <span className="text-xs font-medium text-success">DPR</span>
                  </Link>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {hasModuleAccess("materials") && (
                  <Link 
                    to={`/materials/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-warning/5 hover:bg-warning/10 transition-colors text-center"
                  >
                    <Package className="w-5 h-5 text-warning mb-1" />
                    <span className="text-xs font-medium text-warning">Materials</span>
                  </Link>
                  )}
                  {hasModuleAccess("equipment") && (
                  <Link 
                    to={`/equipment/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-slate-100 hover:bg-slate-200 transition-colors text-center"
                  >
                    <Truck className="w-5 h-5 text-slate-600 mb-1" />
                    <span className="text-xs font-medium text-slate-600">Equipment</span>
                  </Link>
                  )}
                  {hasModuleAccess("workforce") && (
                  <Link 
                    to={`/workforce/${project.id}`}
                    className="flex flex-col items-center p-2 rounded-sm bg-error/5 hover:bg-error/10 transition-colors text-center"
                  >
                    <Users className="w-5 h-5 text-error mb-1" />
                    <span className="text-xs font-medium text-error">Workforce</span>
                  </Link>
                  )}
                </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;